import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { getSelectionState } from '../selectors/studio';
import { updateSelection } from '../actions/studio';

export function useDraftsSelect(deviations: PapiDeviation[]) {
  const dispatch = useDispatch();
  const { selectedRows, lastSelectedRow } = useSelector(getSelectionState);
  const selectedCount = selectedRows.length;

  const allIsSelected = useMemo(
    () => deviations.length === selectedCount,
    [deviations, selectedCount]
  );

  const hasSelection = useMemo(() => selectedCount > 0, [selectedCount]);

  const onSelect = useCallback(
    (rowIndex: number, isSelected: boolean, isShiftKey?: boolean) => {
      const updatedSelectedRows = new Set(selectedRows);

      if (isShiftKey && lastSelectedRow !== null) {
        // shift-click: select a range of rows
        const start = Math.min(lastSelectedRow, rowIndex);
        const end = Math.max(lastSelectedRow, rowIndex);
        const range = Array.from({ length: end - start + 1 }, (_, i) => start + i);
        range.forEach(idx => {
          if (isSelected) {
            updatedSelectedRows.add(idx);
          } else {
            updatedSelectedRows.delete(idx);
          }
        });
      } else {
        if (isSelected) {
          updatedSelectedRows.add(rowIndex);
        } else {
          updatedSelectedRows.delete(rowIndex);
        }
      }
      dispatch(
        updateSelection({
          selectedRows: Array.from(updatedSelectedRows),
          lastSelectedRow: rowIndex,
        })
      );
    },
    [dispatch, selectedRows, lastSelectedRow]
  );

  const onSelectAll = useCallback(
    (isSelected: boolean) => {
      dispatch(
        updateSelection({
          selectedRows: isSelected ? deviations.map((_, idx) => idx) : [],
          lastSelectedRow: null,
        })
      );
    },
    [dispatch, deviations]
  );

  return {
    selected: selectedRows,
    selectedCount,
    allIsSelected,
    hasSelection,
    onSelect,
    onSelectAll,
  };
}
